/**
 * DictationService - Web-compatible voice dictation service
 * Handles WebSocket connection to backend, audio streaming, and real-time transcription
 */

interface DictationConfig {
  language?: string;
  context?: string;
  token?: string;
}

interface DictationCallbacks {
  onPartialTranscript?: (text: string) => void;
  onFinalTranscript?: (text: string) => void;
  onEnhancementChunk?: (chunk: string) => void;
  onEnhancementComplete?: (enhanced: string, original: string) => void;
  onError?: (error: string) => void;
  onReady?: () => void;
  onNoCredits?: () => void;
}

export class DictationService {
  private ws: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private isConnected = false;
  private isRecording = false;
  private callbacks: DictationCallbacks = {};
  private audioBuffer: ArrayBuffer[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private recordingStartTime = 0;
  private accumulatedText = "";
  private interimText = "";

  // Backend URL - dynamically construct WebSocket URL from EXPRESS_BACKEND_URL
  private getWebSocketUrl(isPublic: boolean = false): string {
    const backendUrl =
      process.env.REACT_APP_EXPRESS_BACKEND_URL || "https://api.writevoice.io";
    const path = isPublic ? "/ws/transcription/public" : "/ws/transcription";
    // Replace http:// → ws://, https:// → wss://
    const wsUrl = backendUrl.startsWith("https://")
      ? backendUrl.replace("https://", "wss://")
      : backendUrl.replace("http://", "ws://");
    return wsUrl + path;
  }

  constructor() {}

  /**
   * Connect to the WebSocket backend
   */
  async connect(
    config: DictationConfig,
    callbacks: DictationCallbacks
  ): Promise<void> {
    this.callbacks = callbacks;
    this.accumulatedText = "";
    this.interimText = "";

    // Disconnect any existing connection
    if (this.ws) {
      this.disconnect();
    }

    return new Promise((resolve, reject) => {
      try {
        // Use public endpoint if no token is provided
        const isPublic = !config.token;
        const wsUrl = this.getWebSocketUrl(isPublic);

        // Create WebSocket URL with auth token if available
        const url = new URL(wsUrl);
        if (config.token) {
          url.searchParams.set("token", config.token);
        }

        this.ws = new WebSocket(url.toString());

        this.ws.onopen = () => {
          this.isConnected = true;
          this.reconnectAttempts = 0;

          // Send start message with configuration
          this.sendMessage({
            type: "start",
            language: config.language || "en",
            context: config.context,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });

          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error("[DictationService] Failed to parse message:", error);
          }
        };

        this.ws.onerror = (error) => {
          console.error("[DictationService] WebSocket error:", error);
          if (this.callbacks.onError) {
            this.callbacks.onError("Connection error");
          }
          reject(error);
        };

        this.ws.onclose = (event) => {
          this.isConnected = false;

          // Attempt reconnection for non-intentional closures
          if (
            event.code !== 1000 &&
            this.reconnectAttempts < this.maxReconnectAttempts
          ) {
            this.reconnectAttempts++;
            setTimeout(() => {
              this.connect(config, callbacks).catch((err) => {
                if (this.callbacks.onError) {
                  this.callbacks.onError("Failed to reconnect");
                }
              });
            }, 1000 * this.reconnectAttempts);
          }
        };

        // Binary message handler for audio acknowledgments
        this.ws.binaryType = "arraybuffer";
      } catch (error) {
        console.error("[DictationService] Failed to create WebSocket:", error);
        reject(error);
      }
    });
  }

  /**
   * Start audio capture from microphone
   */
  async startRecording(deviceId?: string): Promise<void> {
    try {
      // If already recording, don't start again
      if (this.isRecording && this.mediaStream) {
        return;
      }

      // Request microphone permission
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 16000,
          channelCount: 1,
          ...(deviceId && { deviceId: { exact: deviceId } }),
        },
        video: false,
      };

      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Create audio context at 16kHz
      this.audioContext = new AudioContext({ sampleRate: 16000 });

      // Create source from microphone
      this.source = this.audioContext.createMediaStreamSource(this.mediaStream);

      // Create script processor for capturing raw PCM (smaller buffer for lower latency)
      this.scriptProcessor = this.audioContext.createScriptProcessor(512, 1, 1);

      // Process audio data
      this.scriptProcessor.onaudioprocess = (event) => {
        if (!this.isRecording) return;

        const inputData = event.inputBuffer.getChannelData(0);

        // Convert Float32Array to Int16Array for backend
        const int16Array = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        // Send audio data to backend
        this.sendAudioData(int16Array.buffer);
      };

      // Connect audio pipeline
      this.source.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);

      this.isRecording = true;
      this.recordingStartTime = Date.now();
      this.audioBuffer = [];

      // Flush any buffered audio
      this.flushAudioBuffer();
    } catch (error) {
      console.error("[DictationService] Failed to start recording:", error);
      throw error;
    }
  }

  /**
   * Stop recording and optionally trigger enhancement
   */
  async stopRecording(enhance: boolean = true): Promise<void> {
    this.isRecording = false;

    // Stop audio capture
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // Disconnect audio nodes
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Send stop message to backend
    if (this.isConnected) {
      this.sendMessage({
        type: "stop",
        enhance,
      });
    }

    // Calculate recording duration
    const duration = this.recordingStartTime
      ? Date.now() - this.recordingStartTime
      : 0;
  }

  /**
   * Send audio data to backend
   */
  private sendAudioData(audioData: ArrayBuffer): void {
    if (
      !this.isConnected ||
      !this.ws ||
      this.ws.readyState !== WebSocket.OPEN
    ) {
      // Buffer audio if not connected yet
      this.audioBuffer.push(audioData);
      return;
    }

    // Send buffered audio first
    this.flushAudioBuffer();

    // Send current audio
    this.ws.send(audioData);
  }

  /**
   * Flush buffered audio data
   */
  private flushAudioBuffer(): void {
    if (this.audioBuffer.length === 0) return;
    if (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN)
      return;

    for (const audioData of this.audioBuffer) {
      this.ws.send(audioData);
    }

    this.audioBuffer = [];
  }

  /**
   * Handle messages from backend
   */
  private handleMessage(message: any): void {
    switch (message.type) {
      case "ready":
        // Flush any buffered audio
        this.flushAudioBuffer();
        if (this.callbacks.onReady) {
          this.callbacks.onReady();
        }
        break;

      case "no_credits":
        if (this.callbacks.onNoCredits) {
          this.callbacks.onNoCredits();
        }
        break;

      case "transcription:partial":
        this.interimText = message.text;
        if (this.callbacks.onPartialTranscript) {
          this.callbacks.onPartialTranscript(message.text);
        }
        break;

      case "transcription:final":
        // Add to accumulated text
        if (this.accumulatedText) {
          this.accumulatedText += " " + message.text;
        } else {
          this.accumulatedText = message.text;
        }
        this.interimText = "";

        if (this.callbacks.onFinalTranscript) {
          this.callbacks.onFinalTranscript(message.text);
        }
        break;

      case "enhancement:chunk":
        if (this.callbacks.onEnhancementChunk) {
          this.callbacks.onEnhancementChunk(message.text);
        }
        break;

      case "enhancement:complete":
        if (this.callbacks.onEnhancementComplete) {
          this.callbacks.onEnhancementComplete(
            message.enhanced || message.text,
            message.original || this.accumulatedText
          );
        }
        break;

      case "error":
      case "transcription:error":
      case "enhancement:error":
        console.error("[DictationService] Error from backend:", message.error);
        if (this.callbacks.onError) {
          this.callbacks.onError(message.error);
        }
        break;

      default:
    }
  }

  /**
   * Send message to backend
   */
  private sendMessage(message: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    this.ws.send(JSON.stringify(message));
  }

  /**
   * Get accumulated text (final transcripts)
   */
  getAccumulatedText(): string {
    return this.accumulatedText;
  }

  /**
   * Get interim text (partial transcript)
   */
  getInterimText(): string {
    return this.interimText;
  }

  /**
   * Check if service is connected
   */
  isConnectedToBackend(): boolean {
    return this.isConnected;
  }

  /**
   * Disconnect from backend
   */
  disconnect(): void {
    // Force stop recording - always cleanup media streams
    this.isRecording = false;

    // Stop audio capture
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStream = null;
    }

    // Disconnect audio nodes
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Close WebSocket
    if (this.ws) {
      this.isConnected = false;
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }

    // Reset state
    this.audioBuffer = [];
    this.accumulatedText = "";
    this.interimText = "";
    this.reconnectAttempts = 0;
  }
}

// Singleton instance
export const dictationService = new DictationService();
